import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  CanvasWrap,
  StackedCanvas,
  Toolbar,
  ToolbarHint,
  EditorShell,
} from "../styles";
import {
  decodeTiffToRgba,
  paintRgbaToCanvas,
  polygonsToFeatureCollection,
  featureCollectionToPolygons,
} from "../../../../utils/tiffCanvas";

/**
 * Painel de edição: carrega TIFF, desenha polígonos de supressão.
 *
 * Cliques adicionam vértices; "Fechar polígono" finaliza o anel atual;
 * "Salvar indicação" devolve FeatureCollection normalizada 0–1.
 */
const TiffSuppressionEditor = ({
  tiffUrl,
  tiffFile,
  initialGeometry,
  onGeometryChange,
  readOnly = false,
}) => {
  const baseRef = useRef(null);
  const overlayRef = useRef(null);
  const [meta, setMeta] = useState(null);
  const [polygons, setPolygons] = useState([]);
  const [draft, setDraft] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setPolygons(featureCollectionToPolygons(initialGeometry));
  }, [initialGeometry]);

  const drawOverlay = useCallback(() => {
    const canvas = overlayRef.current;
    if (!canvas || !meta) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const toPx = ([nx, ny]) => [nx * canvas.width, ny * canvas.height];

    const paintRing = (ring, color, fill) => {
      if (!ring?.length) return;
      ctx.beginPath();
      ring.forEach((p, i) => {
        const [x, y] = toPx(p);
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.closePath();
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.stroke();
      if (fill) {
        ctx.fillStyle = fill;
        ctx.fill();
      }
      ring.forEach((p) => {
        const [x, y] = toPx(p);
        ctx.beginPath();
        ctx.arc(x, y, 4, 0, Math.PI * 2);
        ctx.fillStyle = color;
        ctx.fill();
      });
    };

    polygons.forEach((ring) =>
      paintRing(ring, "#ff6b4a", "rgba(255, 80, 60, 0.28)")
    );
    if (draft.length) {
      paintRing(draft, "#ffd166", "rgba(255, 209, 102, 0.2)");
    }
  }, [draft, meta, polygons]);

  useEffect(() => {
    drawOverlay();
  }, [drawOverlay]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!tiffUrl && !tiffFile) return;
      setLoading(true);
      setError("");
      try {
        let buffer;
        if (tiffFile) {
          buffer = await tiffFile.arrayBuffer();
        } else {
          const res = await fetch(tiffUrl, { credentials: "include" });
          if (!res.ok) throw new Error(`Falha ao baixar TIFF (${res.status})`);
          buffer = await res.arrayBuffer();
        }
        const { width, height, rgba } = await decodeTiffToRgba(buffer);
        if (cancelled) return;
        const painted = paintRgbaToCanvas(baseRef.current, width, height, rgba);
        if (overlayRef.current) {
          overlayRef.current.width = painted.displayWidth;
          overlayRef.current.height = painted.displayHeight;
        }
        setMeta(painted);
      } catch (err) {
        console.error(err);
        if (!cancelled) setError(err.message || "Não foi possível carregar o TIFF.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [tiffUrl, tiffFile]);

  const emit = (nextPolygons) => {
    const fc = polygonsToFeatureCollection(nextPolygons);
    onGeometryChange?.(fc, meta);
  };

  const handleClick = (e) => {
    if (readOnly || !meta) return;
    const canvas = overlayRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    setDraft((prev) => [...prev, [x, y]]);
  };

  const closePolygon = () => {
    if (draft.length < 3) return;
    const next = [...polygons, draft];
    setPolygons(next);
    setDraft([]);
    emit(next);
  };

  const undoPoint = () => {
    if (draft.length) {
      setDraft((d) => d.slice(0, -1));
      return;
    }
    const next = polygons.slice(0, -1);
    setPolygons(next);
    emit(next);
  };

  const clearAll = () => {
    setPolygons([]);
    setDraft([]);
    emit([]);
  };

  return (
    <EditorShell>
      {!readOnly && (
        <Toolbar>
          <button type="button" onClick={closePolygon} disabled={draft.length < 3}>
            Fechar polígono
          </button>
          <button type="button" onClick={undoPoint}>
            Desfazer
          </button>
          <button type="button" onClick={clearAll}>
            Limpar indicação
          </button>
          <ToolbarHint>
            Clique na imagem para marcar vértices da área de supressão.
          </ToolbarHint>
        </Toolbar>
      )}
      {loading && <ToolbarHint>Carregando TIFF…</ToolbarHint>}
      {error && (
        <ToolbarHint style={{ color: "var(--danger)" }}>{error}</ToolbarHint>
      )}
      <CanvasWrap>
        <StackedCanvas>
          <canvas ref={baseRef} />
          <canvas ref={overlayRef} className="overlay" onClick={handleClick} />
        </StackedCanvas>
      </CanvasWrap>
    </EditorShell>
  );
};

export default TiffSuppressionEditor;
